import { FunctionComponent, ReactElement, useEffect } from "react";

import {
    Autocomplete,
    Chip,
    FormControlLabel,
    MenuItem,
    Paper,
    Switch,
    TextField,
    Typography,
    styled,
} from "@mui/material";

import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";

import { CircularLoader, StickyBottomBar } from "../../components/common";
import {
    useCreateIngredient,
    useDeleteIngredient,
    useGetIngredient,
    useGetIngredientCategories,
    useUpdateIngredient,
} from "../../hooks";
import { useMessage } from "../../sdk/hooks";
import { IngredientBaseUnit, IngredientStatus } from "../../sdk/types/api";
import { TIngredientPayload } from "../../sdk/utils/api/ingredients";
import {
    ingredientBaseUnitNames,
    ingredientQueryKeys,
    ingredientStatusNames,
} from "../ingredients/constants";

const Root = styled("main")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};

    max-width: 800px;
    padding: ${({ theme }) => theme.spacing(3)};
    margin: ${({ theme }) => theme.spacing(0, "auto")};
`;

const Title = styled(Typography)`
    font-size: 24px;
    font-weight: 500;
`;

const Section = styled(Paper)`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2.5)};

    padding: ${({ theme }) => theme.spacing(3)};
`;

const SectionTitle = styled(Typography)`
    font-size: 16px;
    font-weight: 500;
`;

const Row = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};

    & > * {
        flex: 1;
    }
`;

const Hint = styled(Typography)`
    font-size: 13px;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

interface IIngredientForm {
    name: string;
    categoryId: string;
    baseUnit: IngredientBaseUnit;
    gramsPerPiece: string;
    isStaple: boolean;
    status: IngredientStatus;
    aliases: string[];
}

const validationSchema = Yup.object({
    name: Yup.string().trim().max(120).required("Name is required"),
    gramsPerPiece: Yup.number()
        .transform((value, original) => (original === "" ? undefined : value))
        .positive("Must be greater than 0"),
});

const emptyForm: IIngredientForm = {
    name: "",
    categoryId: "",
    baseUnit: IngredientBaseUnit.GRAM,
    gramsPerPiece: "",
    isStaple: false,
    status: IngredientStatus.ACTIVE,
    aliases: [],
};

export const IngredientScreen: FunctionComponent = (): ReactElement => {
    const params = useParams();
    const navigate = useNavigate();
    const ingredientId = params.id ?? "";
    const isNew = ingredientId === "new";
    const { showError, showSuccess } = useMessage();

    const ingredientQuery = useGetIngredient([ingredientQueryKeys.detail, ingredientId], ingredientId, {
        enabled: !isNew,
    });
    const categoriesQuery = useGetIngredientCategories(ingredientQueryKeys.categories);
    const createMutation = useCreateIngredient(ingredientQueryKeys.list);
    const updateMutation = useUpdateIngredient([ingredientQueryKeys.detail, ingredientId]);
    const deleteMutation = useDeleteIngredient(ingredientQueryKeys.list);

    const formik = useFormik<IIngredientForm>({
        initialValues: emptyForm,
        validationSchema,
        onSubmit: async (values) => {
            const body: TIngredientPayload = {
                name: values.name.trim(),
                categoryId: values.categoryId || null,
                baseUnit: values.baseUnit,
                gramsPerPiece: values.gramsPerPiece === "" ? null : Number(values.gramsPerPiece),
                isStaple: values.isStaple,
                status: values.status,
                aliases: values.aliases,
            };
            try {
                if (isNew) {
                    const created = await createMutation.mutateAsync(body);
                    showSuccess("Ingredient created");
                    navigate(`/ingredients/${created.id}`, { replace: true });
                } else {
                    await updateMutation.mutateAsync({ ingredientId, body });
                    showSuccess("Ingredient saved");
                }
            } catch (error) {
                showError(error);
            }
        },
    });

    const { setValues } = formik;
    useEffect(() => {
        const ingredient = ingredientQuery.data;
        if (!ingredient || isNew) {
            return;
        }
        setValues({
            name: ingredient.name,
            categoryId: ingredient.categoryId ?? "",
            baseUnit: ingredient.baseUnit,
            gramsPerPiece: ingredient.gramsPerPiece === null ? "" : String(ingredient.gramsPerPiece),
            isStaple: ingredient.isStaple,
            status: ingredient.status,
            aliases: ingredient.aliases,
        });
    }, [ingredientQuery.data, isNew, setValues]);

    const handleDelete = async () => {
        if (isNew) {
            navigate("/ingredients");
            return;
        }
        if (!window.confirm(`Delete "${formik.values.name}"? Recipes using it will show it as unmatched.`)) {
            return;
        }
        try {
            await deleteMutation.mutateAsync({ ingredientId });
            showSuccess("Ingredient deleted");
            navigate("/ingredients");
        } catch (error) {
            showError(error);
        }
    };

    if (!isNew && ingredientQuery.isLoading) {
        return <CircularLoader />;
    }

    const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik;

    return (
        <>
            <Root>
                <Title>{isNew ? "New ingredient" : values.name || "Ingredient"}</Title>

                <Section>
                    <SectionTitle>Details</SectionTitle>
                    <TextField
                        name="name"
                        label="Name"
                        size="small"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                    />
                    <Row>
                        <TextField
                            select
                            name="categoryId"
                            label="Category"
                            size="small"
                            value={values.categoryId}
                            onChange={handleChange}
                        >
                            <MenuItem value="">Other</MenuItem>
                            {(categoriesQuery.data ?? []).map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                    {`${category.icon ?? ""} ${category.name}`.trim()}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            name="status"
                            label="Status"
                            size="small"
                            value={values.status}
                            onChange={handleChange}
                        >
                            {[IngredientStatus.ACTIVE, IngredientStatus.INACTIVE].map((status) => (
                                <MenuItem key={status} value={status}>
                                    {ingredientStatusNames[status]}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Row>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={values.isStaple}
                                onChange={(event) => setFieldValue("isStaple", event.target.checked)}
                            />
                        }
                        label="Pantry staple (shown collapsed in the app, e.g. salt, oil)"
                    />
                </Section>

                <Section>
                    <SectionTitle>Quantities</SectionTitle>
                    <Hint>
                        Recipe amounts are converted into the base unit before they are added up.
                        Grams per piece lets "2 onions" and "300 g onion" merge into one line.
                    </Hint>
                    <Row>
                        <TextField
                            select
                            name="baseUnit"
                            label="Base unit"
                            size="small"
                            value={values.baseUnit}
                            onChange={handleChange}
                        >
                            {[IngredientBaseUnit.GRAM, IngredientBaseUnit.MILLILITRE, IngredientBaseUnit.PIECE].map(
                                (unit) => (
                                    <MenuItem key={unit} value={unit}>
                                        {ingredientBaseUnitNames[unit]}
                                    </MenuItem>
                                ),
                            )}
                        </TextField>
                        <TextField
                            name="gramsPerPiece"
                            label="Grams per piece"
                            size="small"
                            type="number"
                            value={values.gramsPerPiece}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.gramsPerPiece && Boolean(errors.gramsPerPiece)}
                            helperText={(touched.gramsPerPiece && errors.gramsPerPiece) || "Optional"}
                        />
                    </Row>
                </Section>

                <Section>
                    <SectionTitle>Aliases</SectionTitle>
                    <Hint>
                        Other names recipes use for this ingredient (Hindi names, spellings, brands).
                        Press Enter after each.
                    </Hint>
                    <Autocomplete
                        multiple
                        freeSolo
                        options={[] as string[]}
                        value={values.aliases}
                        onChange={(_event, value) =>
                            setFieldValue(
                                "aliases",
                                value.map((alias) => alias.trim()).filter(Boolean),
                            )
                        }
                        renderTags={(value, getTagProps) =>
                            value.map((alias, index) => (
                                <Chip {...getTagProps({ index })} key={alias} label={alias} size="small" />
                            ))
                        }
                        renderInput={(inputParams) => (
                            <TextField {...inputParams} size="small" placeholder="e.g. pyaz" />
                        )}
                    />
                </Section>
            </Root>

            <StickyBottomBar
                cancelText={isNew ? "Discard" : "Delete"}
                submitText="Save"
                onCancel={handleDelete}
                onSubmit={() => formik.handleSubmit()}
                loading={createMutation.isLoading || updateMutation.isLoading}
            />
        </>
    );
};
